import {
  amountFor,
  isIncluded,
  paymentIssue,
  totalsFor,
  money,
} from "./payrollModel";

export function monthlyPayrolls(state) {
  const months = [...state.runs];
  if (!months.some((run) => run.period === state.period)) {
    months.push({
      period: state.period,
      date: state.paymentDate,
      draft: true,
      entries: state.employees
        .filter(
          (e) =>
            e.active && isIncluded(state, e) && !state.excluded?.includes(e.id),
        )
        .map((e) => ({
          employeeId: e.id,
          employeeNumber: e.employeeId,
          name: e.name,
          currency: e.currency,
          amount: amountFor(state, e),
          status: paymentIssue(state, e) || "Ready",
        })),
    });
  }
  return months.sort((a, b) => b.period.localeCompare(a.period));
}

export function payrollStatus(month) {
  if (month.purged) return "Retention period ended";
  if (month.draft) return "Draft";
  const statuses = {
    sending: "Confirming delivery",
    unknown: "Confirmation needed",
    received: "Received by bank",
    processing: "Bank processing",
    paid: "Paid · bank confirmed",
    rejected: "Rejected by bank",
  };
  return statuses[month.status] || "Awaiting bank confirmation";
}

// Rasterize non-Latin names with the browser font so Arabic names remain readable.
// The numeric amounts and report metadata remain searchable vector text.
function employeeName(doc, value, x, y, width) {
  if (/^[\x20-\x7E]*$/.test(value)) {
    const lines = doc.splitTextToSize(value, width);
    doc.text(lines, x, y);
    return Math.max(1, lines.length) * 4;
  }
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const scale = 5;
  canvas.width = width * scale;
  context.font = "17px Arial, sans-serif";
  const lines = [];
  let line = "";
  for (const char of value) {
    if (context.measureText(line + char).width > canvas.width - 6 && line) {
      lines.push(line);
      line = "";
    }
    line += char;
  }
  if (line) lines.push(line);
  canvas.height = Math.max(1, lines.length) * 23;
  context.font = "17px Arial, sans-serif";
  context.fillStyle = "#202d40";
  context.textBaseline = "top";
  lines.forEach((text, index) => context.fillText(text, 0, index * 23));
  doc.addImage(
    canvas.toDataURL("image/png"),
    "PNG",
    x,
    y - 3,
    width,
    canvas.height / scale,
  );
  return canvas.height / scale;
}

export function buildPayrollPDF(month) {
  const { jsPDF } = require("jspdf");
  const doc = new jsPDF();
  const label = new Date(`${month.period}-01T12:00:00`).toLocaleDateString(
    "en-US",
    { month: "long", year: "numeric" },
  );
  doc.setProperties({
    title: `JOBSEEQR Payroll - ${label}`,
    subject: "Confidential employee payment breakdown",
    creator: "JOBSEEQR",
  });
  const header = (continued = false) => {
    doc.setTextColor(32, 45, 64);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(19);
    doc.text("JOBSEEQR", 18, 22);
    doc.setTextColor(86, 141, 237);
    doc.setFontSize(10);
    doc.text("PAYROLL", 192, 22, { align: "right" });
    doc.setDrawColor(86, 141, 237);
    doc.line(18, 28, 192, 28);
    doc.setTextColor(32, 45, 64);
    doc.setFontSize(18);
    doc.text(label, 18, 41);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `${payrollStatus(month)} | ${month.entries.length} employees${continued ? " | Continued" : ""}`,
      18,
      49,
    );
    doc.setTextColor(100);
    doc.text(
      month.draft
        ? "DRAFT - not submitted to the bank."
        : "Payment status reflects bank confirmation; this report is not a bank receipt.",
      18,
      56,
    );
    doc.setFillColor(240, 245, 253);
    doc.rect(18, 63, 174, 10, "F");
    doc.setTextColor(32, 45, 64);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Employee / ID", 21, 69);
    doc.text("Amount", 143, 69, { align: "right" });
    doc.text("Status", 151, 69);
  };
  header();
  let y = 81;
  for (const entry of month.entries) {
    const name = String(entry.name || "Unnamed employee");
    const nameLines = /^[\x20-\x7E]*$/.test(name)
      ? doc.splitTextToSize(name, 72).length
      : Math.ceil(name.length / 28);
    const labels = {
      awaiting_bank: "Awaiting bank confirmation",
      paid: "Paid",
      rejected: "Rejected by bank",
    };
    const status = doc.splitTextToSize(
      labels[entry.status] || String(entry.status || "Draft"),
      36,
    );
    const height = Math.max(18, nameLines * 4 + 10, status.length * 4 + 7);
    if (y + height > 266) {
      doc.addPage();
      header(true);
      y = 81;
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(32, 45, 64);
    const nameHeight = employeeName(doc, name, 21, y, 72);
    doc.setTextColor(110);
    doc.setFontSize(8);
    employeeName(
      doc,
      String(entry.employeeNumber || "No employee ID"),
      21,
      y + nameHeight + 1,
      72,
    );
    doc.setTextColor(32, 45, 64);
    doc.setFontSize(9);
    doc.text(
      entry.amount == null ? "Not set" : money(entry.amount, entry.currency),
      143,
      y,
      { align: "right" },
    );
    doc.text(status, 151, y);
    y += Math.max(height, nameHeight + 11);
    doc.setDrawColor(226, 231, 239);
    doc.line(18, y - 6, 192, y - 6);
  }
  const totals = Object.entries(totalsFor(month.entries));
  if (y + 22 + totals.length * 7 > 267) {
    doc.addPage();
    header(true);
    y = 81;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(
    month.entries.some((e) => e.amount == null)
      ? "TOTALS - entered amounts only"
      : "TOTAL PAYROLL",
    21,
    y + 4,
  );
  totals.forEach(([currency, amount], index) =>
    doc.text(money(amount, currency), 192, y + 14 + index * 7, {
      align: "right",
    }),
  );
  if (!month.entries.length) {
    doc.setFont("helvetica", "normal");
    doc.text("No employees included in this month.", 21, y + 14);
  }
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text("Confidential - employee payment breakdown", 18, 283);
    doc.text(`${page} / ${pages}`, 192, 283, { align: "right" });
  }
  return doc;
}
export function downloadPayrollPDF(month) {
  const url = URL.createObjectURL(buildPayrollPDF(month).output("blob"));
  const link = document.createElement("a");
  link.href = url;
  link.download = `JOBSEEQR-payroll-${month.period}${month.draft ? "-draft" : ""}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Allow embedded browsers time to consume the blob before releasing it.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
